// JOBE-0098.3: localStorage-backed recent queries hook. v1 storage is
// per-browser; a future graph `ui-pref` entity replaces the persistence
// without changing this hook's surface (same pattern as
// useLibraryPreferences in JOBE-0044.6).
//
// Stored shape: see JOBE-0098.3 §Decision: Recents storage.

import { useCallback, useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'omni-palette.recent';
const MAX_RECENTS = 3;

function readStorage() {
  if (typeof window === 'undefined' || !window.localStorage) return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((r) => r && typeof r.query === 'string').slice(0, MAX_RECENTS);
  } catch {
    return [];
  }
}

function writeStorage(items) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // ignore quota / serialization errors
  }
}

export function useRecentQueries() {
  const [recents, setRecents] = useState(() => readStorage());
  // Skip the initial-hydration write — the state was just read from
  // storage on mount, so re-writing it is redundant I/O on every
  // palette mount (PR #696 NIT).
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (!hydratedRef.current) {
      hydratedRef.current = true;
      return;
    }
    writeStorage(recents);
  }, [recents]);

  const push = useCallback((entry) => {
    if (!entry || typeof entry.query !== 'string' || !entry.query.trim()) return;
    setRecents((prev) => {
      const now = Date.now();
      const normalized = {
        id: entry.id || `r${now}`,
        label: (entry.label || entry.query).slice(0, 60),
        query: entry.query,
        chips: Array.isArray(entry.chips) ? entry.chips : [],
        savedAt: now,
      };
      const deduped = prev.filter((r) => r.query !== normalized.query);
      const merged = [normalized, ...deduped].slice(0, MAX_RECENTS);
      return merged;
    });
  }, []);

  const clear = useCallback(() => {
    setRecents([]);
  }, []);

  return { recents, push, clear };
}
